import { WaterlilyClient, createQueryKeys } from '@client/api/waterlily';
import type { API } from '@client/api/types';

export const inviteKeys = createQueryKeys('invite');

export const inviteApi = {
  sendInvite: (data: IntakeFormEmailProps) =>
    WaterlilyClient.post<{
      success: boolean;
      result?: unknown;
      error?: string;
      intakeFormLink?: string;
      clientId?: string;
      hasPrefillPlan?: boolean;
    }>('/intake-form-email', data),
  sendBatchInvite: (data: BatchIntakeFormEmailProps) =>
    WaterlilyClient.post<BatchEmailReminderResult>('/batch-intake-form-email', data),
};

export interface QuickClientWithFormRequest {
  advisorId: string;
  firstName: string;
  lastName: string;
  email?: string;
  clientRelationshipStage?: ClientRelationshipStage;
  acknowledgedSelfPlanConsent?: boolean;
}

export interface QuickClientWithFormResponse {
  clientId: string;
  formReferenceId: string;
}

export const quickClientKeys = createQueryKeys('quickClient');

export const quickClientApi = {
  createQuickClientWithForm: (data: QuickClientWithFormRequest) =>
    WaterlilyClient.post<QuickClientWithFormResponse>(
      `/advisor/${data.advisorId}/quick-client-with-form`,
      data,
    ),
};

export const agentEnteredIntakeKeys = createQueryKeys('agentEnteredIntake');

export interface CreateAgentEnteredIntakeRequest {
  advisorId: string;
  firstName: string;
  lastName: string;
  email: string;
  /** `YYYY-MM-DD`. Compared as a calendar date on the server, never as an instant. */
  birthDate: string;
  intakeMode: 'intake-form' | 'short-form';
}

export interface CreateAgentEnteredIntakeForClientRequest {
  clientId: string;
  email: string;
  birthDate: string;
}

/**
 * Opening and tracking a case where the agent enters the customer's information for them.
 *
 * `create` deliberately returns no form link and no `formReferenceId`: there is nothing here an
 * advisor could navigate to before the customer has given permission.
 */
export const agentEnteredIntakeApi = {
  create: ({ advisorId, ...data }: CreateAgentEnteredIntakeRequest) =>
    WaterlilyClient.post<API.AgentEnteredIntakeCreated>(
      `/advisor/${advisorId}/agent-entered-intake`,
      data,
    ),
  createForClient: ({ clientId, ...data }: CreateAgentEnteredIntakeForClientRequest) =>
    WaterlilyClient.post<API.AgentEnteredIntakeCreated>(
      `/client/${clientId}/agent-entered-intake`,
      data,
    ),
  resend: (requestId: string) =>
    WaterlilyClient.post<API.AgentEnteredIntakeCreated & { sentCount: number }>(
      `/agent-entered-intake/${requestId}/resend`,
      {},
    ),
  status: (requestId: string) =>
    WaterlilyClient.get<API.AgentEnteredIntakeRequest>(`/agent-entered-intake/${requestId}`),
  list: (advisorId: string) =>
    WaterlilyClient.get<API.AgentEnteredIntakeRequest[]>(
      `/advisor/${advisorId}/agent-entered-intakes`,
    ),
  cancel: (requestId: string) =>
    WaterlilyClient.delete<{ agentEnteredIntakeId: string; status: string }>(
      `/agent-entered-intake/${requestId}`,
    ),
};
